/**
 * LinkedIn Insight Tag conversion tracking.
 *
 * The tag is privacy-gated: `initLinkedInInsight` is only called after the user
 * has accepted cookies. Until then `window.lintrk` is undefined and
 * `trackLinkedInLead` is a no-op.
 */

const linkedInPartnerId = '9889034';
const linkedInLeadConversionId = 31012546;
const linkedInSignupConversionId = 31012554;
const insightSrc = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';

type Lintrk = {
  (action: string, data: Record<string, unknown>): void;
  q?: unknown[][];
};

declare global {
  interface Window {
    _linkedin_partner_id?: string;
    _linkedin_data_partner_ids?: string[];
    lintrk?: Lintrk;
  }
}

let isInitialized = false;

/**
 * Replicates LinkedIn's official bootstrap snippet: stubs `window.lintrk` with
 * a queue and asynchronously loads `insight.min.js`. Idempotent.
 */
function loadInsight(): void {
  if (window.lintrk) {
    return;
  }

  window._linkedin_partner_id = linkedInPartnerId;
  window._linkedin_data_partner_ids ??= [];
  window._linkedin_data_partner_ids.push(linkedInPartnerId);

  const queue: unknown[][] = [];
  const lintrk: Lintrk = (action, data) => {
    queue.push([action, data]);
  };
  lintrk.q = queue;
  window.lintrk = lintrk;

  const script = document.createElement('script');
  script.async = true;
  script.src = insightSrc;
  document.head.appendChild(script);
}

/**
 * Initialises the LinkedIn Insight Tag. MUST only be called after the user has
 * accepted cookies. Safe to call multiple times.
 */
export function initLinkedInInsight(): void {
  if (typeof window === 'undefined' || isInitialized) {
    return;
  }
  isInitialized = true;

  loadInsight();
}

/**
 * Tracks a successful waitlist signup as a LinkedIn conversion.
 */
export function trackLinkedInLead(): void {
  trackLinkedInConversion(linkedInLeadConversionId);
}

/**
 * Tracks a successful account signup as its dedicated LinkedIn conversion.
 */
export function trackLinkedInSignup(): void {
  trackLinkedInConversion(linkedInSignupConversionId);
}

const trackLinkedInConversion = (conversionId: number): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.lintrk?.('track', { conversion_id: conversionId });
};
